import React, { useState, useEffect, useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
  Popover,
  Grid,
  OutlinedInput,
  CircularProgress
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import palette from 'src/theme/palette';
import {
  getStatus,
  getTypes,
  getListing,
  getSpecifications,
  getAreaUnits,
  getAdvancePrice
} from 'src/ApiServices';
import { set } from 'lodash';

const FilterDrawer = ({ open, onClose, onApplyFilters }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // API data states
  const [statusOptions, setStatusOptions] = useState([]);
  const [areaUnits, setAreaUnits] = useState('');
  const [typeOptions, setTypeOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 1000000]);
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [specifications, setSpecifications] = useState('');
  const [buildYear, setBuildYear] = useState('');
  const [bedrooms, setBedrooms] = useState('');
  const [garage, setGarage] = useState('');
  const [bathrooms, setBathrooms] = useState('');
  const [availableFor, setAvailableFor] = useState('');
  const [isGarage, setIsGarage] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);
  const [specificationsOptions, setSpecificationsOptions] = useState([]);
  const [listingOptions, setListingOptions] = useState([]);
  const [areaUnitsOptions, setAreaUnitsOptions] = useState([]);
  const [advancePrice, setAdvancePrice] = useState([]);
  const [selectedSpecifications, setSelectedSpecifications] = useState([]);
  const [specificationsSearch, setSpecificationsSearch] = useState('');
  const [specificationsAnchorEl, setSpecificationsAnchorEl] = useState(null);
  const [yearAnchorEl, setYearAnchorEl] = useState(null);
  const yearPickerOpen = Boolean(yearAnchorEl);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 50 }, (_, i) => currentYear - i);

  const extractArrayData = (response) => {
    if (!response) return [];

    console.log('Extracting data from:', response);

    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      return response.data.data;
    }

    if (Array.isArray(response.data)) {
      return response.data;
    }

    if (Array.isArray(response)) {
      return response;
    }

    if (typeof response === 'object') {
      for (let key in response) {
        if (Array.isArray(response[key])) {
          return response[key];
        }

        if (response[key] && typeof response[key] === 'object') {
          for (let nestedKey in response[key]) {
            if (Array.isArray(response[key][nestedKey])) {
              return response[key][nestedKey];
            }
          }
        }
      }
    }

    console.warn('Could not extract array data from response:', response);
    return [];
  };

  // Fetch status
  // Replace your useEffect with this updated version:

  // useEffect(() => {
  //   const fetchFilterOptions = async () => {
  //     try {
  //       setLoading(true);
  //       const [statusRes, typesRes, specificationsRes, listingRes, areaUnitsRes, advancePriceRes] =
  //         await Promise.all([
  //           getStatus(),
  //           getTypes(),
  //           getSpecifications(),
  //           getListing(),
  //           getAreaUnits(),
  //           getAdvancePrice()
  //         ]);

  //       console.log('API Responses:', {
  //         statusRes,
  //         typesRes,
  //         specificationsRes,
  //         listingRes,
  //         areaUnitsRes,
  //         advancePriceRes
  //       });

  //       // Use the helper function to safely extract array data
  //       const statusData = extractArrayData(statusRes);
  //       const typesData = extractArrayData(typesRes);
  //       const specificationsData = extractArrayData(specificationsRes);
  //       const listingData = extractArrayData(listingRes);
  //       const areaUnitsData = extractArrayData(areaUnitsRes);

  //       // Handle price range separately (it's an object, not an array)
  //       let priceData = { min_price: 0, max_price: 1000000 }; // default values

  //       if (advancePriceRes?.data?.data) {
  //         priceData = advancePriceRes.data.data;
  //       } else if (advancePriceRes?.data) {
  //         priceData = advancePriceRes.data;
  //       }

  //       console.log('Extracted Data:', {
  //         statusData,
  //         typesData,
  //         specificationsData,
  //         listingData,
  //         areaUnitsData,
  //         priceData
  //       });

  //       setStatusOptions(statusData);
  //       setTypeOptions(typesData);
  //       setSpecificationsOptions(specificationsData);
  //       setListingOptions(listingData);
  //       setAreaUnitsOptions(areaUnitsData);

  //       // Set price range from API response
  //       if (priceData.min_price !== undefined && priceData.max_price !== undefined) {
  //         setPriceRange([priceData.min_price, priceData.max_price]);
  //         setAdvancePrice(priceData); // Store the price object
  //       }
  //     } catch (error) {
  //       console.error('Failed to fetch filter options:', error);
  //       setStatusOptions([]);
  //       setTypeOptions([]);
  //       setSpecificationsOptions([]);
  //       setListingOptions([]);
  //       setAreaUnitsOptions([]);
  //       // Keep default price range on error
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   if (open) {
  //     fetchFilterOptions();
  //   }
  // }, [open]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      setLoading(true);

      try {
        // STATUS
        const statusRes = await getStatus();
        setStatusOptions(extractArrayData(statusRes));
      } catch (err) {
        console.error('Status API error:', err);
        setStatusOptions([]);
      }

      try {
        // TYPES
        const typesRes = await getTypes();
        setTypeOptions(extractArrayData(typesRes));
      } catch (err) {
        console.error('Types API error:', err);
        setTypeOptions([]);
      }
      try {
        // SPECIFICATIONS
        const specificationsRes = await getSpecifications();
        setSpecificationsOptions(extractArrayData(specificationsRes));
      } catch (err) {
        console.error('Specifications API error:', err);
        setSpecificationsOptions([]);
      }

      try {
        // LISTING
        const listingRes = await getListing();
        setListingOptions(extractArrayData(listingRes));
      } catch (err) {
        console.error('Listing API error:', err);
        setListingOptions([]);
      }

      try {
        // AREA UNITS
        const areaUnitsRes = await getAreaUnits();
        setAreaUnitsOptions(extractArrayData(areaUnitsRes));
      } catch (err) {
        console.error('Area Units API error:', err);
        setAreaUnitsOptions([]);
      }

      try {
        // PRICE RANGE
        const advancePriceRes = await getAdvancePrice();

        let priceData = { min_price: 0, max_price: 1000000 };

        if (advancePriceRes?.data?.data) {
          priceData = advancePriceRes.data.data;
        } else if (advancePriceRes?.data) {
          priceData = advancePriceRes.data;
        }

        setAdvancePrice(priceData);
        setPriceRange([priceData.min_price, priceData.max_price]);
      } catch (err) {
        console.error('Advance Price API error:', err);
        setAdvancePrice({ min_price: 0, max_price: 1000000 });
        setPriceRange([0, 1000000]);
      }

      setLoading(false);
    };

    if (open) {
      fetchFilterOptions();
    }
  }, [open]);

  const handlePriceChange = (event, newValue) => {
    setPriceRange(newValue);
  };
  const handleSpecificationsToggle = (specName) => {
    setSelectedSpecifications((prev) => {
      if (prev.includes(specName)) {
        return prev.filter((item) => item !== specName);
      } else {
        return [...prev, specName];
      }
    });
  };

  const handleClearSpecifications = () => {
    setSelectedSpecifications([]);
    setSpecificationsSearch('');
  };

  const filteredSpecifications = useMemo(() => {
    if (!Array.isArray(specificationsOptions)) return [];

    return specificationsOptions.filter((spec) =>
      spec.name.toLowerCase().includes(specificationsSearch.toLowerCase())
    );
  }, [specificationsOptions, specificationsSearch]);

  useEffect(() => {
    if (selectedSpecifications.length > 0) {
      setSpecifications(selectedSpecifications.join(','));
    } else {
      setSpecifications('');
    }
  }, [selectedSpecifications]);
  const handleSpecificationsInputClick = (event) => {
    setSpecificationsAnchorEl(event.currentTarget);
  };

  const handleCloseSpecifications = () => {
    setSpecificationsAnchorEl(null);
  };
  const handleApplyFilters = () => {
    const filters = {
      priceRange,
      type: type || '',
      status: status || '',
      specifications: selectedSpecifications.length > 0 ? selectedSpecifications.join(',') : '',
      buildYear: buildYear || '',
      bedrooms: bedrooms || '',
      availableFor: availableFor || '',
      isFeatured,
      bathrooms,
      garage: isGarage
    };

    console.log('Applying filters:', filters);
    onApplyFilters(filters);
    onClose();
  };

  const handleResetFilters = () => {
    setPriceRange([0, 1000000]);
    setType('');
    setStatus('');
    setSpecifications('');
    setSelectedSpecifications([]);
    setSpecificationsSearch('');
    setBuildYear('');
    setBedrooms('');
    setAvailableFor('');
    setIsFeatured(false);
    setGarage('');
    setBathrooms('');
    setIsGarage(false);
    onApplyFilters({});
    onClose();
  };
  const formatPrice = (value) => {
    return `$${value.toLocaleString()}`;
  };

  const handleYearSelect = (year) => {
    setBuildYear(year);
    setYearAnchorEl(null);
  };

  const openYearPicker = (event) => {
    setYearAnchorEl(event.currentTarget);
  };

  const closeYearPicker = () => {
    setYearAnchorEl(null);
  };

  const clearBuildYear = () => {
    setBuildYear('');
    setYearAnchorEl(null);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: isMobile ? '100%' : 400,
            maxWidth: '100vw',
            background: palette.common.white,
            boxShadow: `0 0 24px ${alpha(palette.common.black, 0.15)}`,
            display: 'flex',
            flexDirection: 'column',
            height: '100vh'
          }
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: `1px solid ${palette.grey[200]}`,
            background: palette.common.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: palette.primary.titledark,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <FilterListIcon />
            Filters
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: palette.grey[600],
              '&:hover': {
                background: palette.grey[100],
                color: palette.primary.main
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Filter Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 3
          }}
        >
          {/* Price Range */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                color: palette.primary.title,
                mb: 2,
                fontWeight: 600
              }}
            >
              Price Range
            </Typography>
            {console.log('Price Range:', advancePrice)}
            <Slider
              value={priceRange}
              onChange={handlePriceChange}
              valueLabelDisplay="auto"
              valueLabelFormat={formatPrice}
              min={advancePrice?.min_price || 0}
              max={advancePrice?.max_price || 1000000}
              step={1000}
              sx={{
                color: palette.primary.main,
                '& .MuiSlider-valueLabel': {
                  background: palette.primary.main,
                  color: palette.primary.contrastText
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="body2" color={palette.grey[600]}>
                {formatPrice(priceRange[0])}
              </Typography>
              <Typography variant="body2" color={palette.grey[600]}>
                {formatPrice(priceRange[1])}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3, bgcolor: palette.grey[200] }} />

          {/* Type Dropdown */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel sx={{ color: palette.grey[600] }}>Type</InputLabel>
            <Select
              value={type}
              label="Type"
              onChange={(e) => setType(e.target.value)}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: palette.inputborder
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: palette.primary.main
                }
              }}
              endAdornment={loading ? <CircularProgress size={20} /> : null}
            >
              <MenuItem value="">Any</MenuItem>
              {Array.isArray(typeOptions) &&
                typeOptions.map((typeItem) => (
                  <MenuItem key={typeItem.id || typeItem.name} value={typeItem.id}>
                    {typeItem.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* Status Dropdown */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel sx={{ color: palette.grey[600] }}>Property Status</InputLabel>
            <Select
              value={status}
              label="Property Status"
              onChange={(e) => setStatus(e.target.value)}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: palette.inputborder
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: palette.primary.main
                }
              }}
              endAdornment={loading ? <CircularProgress size={20} /> : null}
            >
              <MenuItem value="">Any</MenuItem>
              {Array.isArray(statusOptions) &&
                statusOptions.map((statusItem) => (
                  <MenuItem key={statusItem.id || statusItem.name} value={statusItem.name}>
                    {statusItem.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* Specifications */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel
              sx={{ color: palette.grey[600] }}
              shrink={selectedSpecifications.length > 0}
            >
              Specifications
            </InputLabel>
            <OutlinedInput
              value={
                selectedSpecifications.length > 0 ? `${selectedSpecifications.length} selected` : ''
              }
              label="Specifications"
              readOnly
              onClick={handleSpecificationsInputClick}
              disabled={loading}
              endAdornment={
                <>
                  {selectedSpecifications.length > 0 && (
                    <IconButton
                      size="small"
                      onClick={handleClearSpecifications}
                      sx={{ mr: 1, color: palette.grey[600] }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                  {loading ? <CircularProgress size={20} /> : null}
                </>
              }
              sx={{
                cursor: 'pointer',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: palette.inputborder
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: palette.primary.main
                }
              }}
            />
          </FormControl>
          <Popover
            open={Boolean(specificationsAnchorEl)}
            anchorEl={specificationsAnchorEl}
            onClose={handleCloseSpecifications}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center'
            }}
            sx={{
              '& .MuiPopover-paper': {
                width: isMobile ? '90vw' : 350,
                maxWidth: '100%',
                maxHeight: 400,
                borderRadius: 2,
                boxShadow: `0 8px 32px ${alpha(palette.common.black, 0.2)}`
              }
            }}
          >
            <Box sx={{ p: 2 }}>
              {/* Search Input */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <OutlinedInput
                  placeholder="Search specifications..."
                  value={specificationsSearch}
                  onChange={(e) => setSpecificationsSearch(e.target.value)}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: palette.inputborder
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: palette.primary.main
                    }
                  }}
                  endAdornment={
                    specificationsSearch && (
                      <IconButton
                        size="small"
                        onClick={() => setSpecificationsSearch('')}
                        sx={{ color: palette.grey[600] }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )
                  }
                />
              </FormControl>

              {/* Clear All Button */}
              {selectedSpecifications.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <Button
                    size="small"
                    onClick={handleClearSpecifications}
                    sx={{
                      color: palette.primary.main,
                      fontSize: '0.75rem',
                      textTransform: 'none'
                    }}
                  >
                    Clear all
                  </Button>
                </Box>
              )}

              {/* Specifications List with Checkboxes */}
              <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : filteredSpecifications.length > 0 ? (
                  filteredSpecifications.map((specItem) => (
                    <Box
                      key={specItem.id || specItem.name}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 1,
                        borderRadius: 1,
                        '&:hover': {
                          backgroundColor: palette.grey[50]
                        }
                      }}
                    >
                      <Checkbox
                        checked={selectedSpecifications.includes(specItem.name)}
                        onChange={() => handleSpecificationsToggle(specItem.name)}
                        size="small"
                        sx={{
                          color: palette.primary.main,
                          '&.Mui-checked': {
                            color: palette.primary.main
                          }
                        }}
                      />
                      <Typography
                        sx={{
                          color: palette.primary.title,
                          fontSize: '0.875rem',
                          flex: 1,
                          cursor: 'pointer'
                        }}
                        onClick={() => handleSpecificationsToggle(specItem.name)}
                      >
                        {specItem.name}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography
                    sx={{
                      color: palette.grey[600],
                      textAlign: 'center',
                      p: 3,
                      fontSize: '0.875rem'
                    }}
                  >
                    {specificationsSearch
                      ? 'No specifications found'
                      : 'No specifications available'}
                  </Typography>
                )}
              </Box>
            </Box>
          </Popover>

          <Divider sx={{ my: 3, bgcolor: palette.grey[200] }} />

          {/* Build Year - Custom Input with Calendar Icon */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel sx={{ color: palette.grey[600] }}>Build Year</InputLabel>
            <OutlinedInput
              value={buildYear}
              label="Build Year"
              readOnly
              onClick={openYearPicker}
              endAdornment={
                <IconButton
                  onClick={openYearPicker}
                  edge="end"
                  sx={{
                    color: palette.grey[600],
                    '&:hover': {
                      color: palette.primary.main,
                      background: 'transparent'
                    }
                  }}
                >
                  <CalendarTodayIcon />
                </IconButton>
              }
              sx={{
                cursor: 'pointer',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: palette.inputborder
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: palette.primary.main
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: palette.primary.main
                }
              }}
            />
          </FormControl>

          {/* Bedrooms */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel sx={{ color: palette.grey[600] }}>Bedrooms</InputLabel>
            <Select
              value={bedrooms}
              label="Bedrooms"
              onChange={(e) => setBedrooms(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: palette.inputborder
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: palette.primary.main
                }
              }}
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value={1}>1</MenuItem>
              <MenuItem value={2}>2</MenuItem>
              <MenuItem value={3}>3</MenuItem>
              <MenuItem value={4}>4+</MenuItem>
            </Select>
          </FormControl>

          {/* Bathrooms */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel sx={{ color: palette.grey[600] }}>Bathrooms</InputLabel>
            <Select
              value={bathrooms}
              label="Bathrooms"
              onChange={(e) => setBathrooms(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: palette.inputborder
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: palette.primary.main
                }
              }}
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value={1}>1</MenuItem>
              <MenuItem value={2}>2</MenuItem>
              <MenuItem value={3}>3</MenuItem>
              <MenuItem value={4}>4+</MenuItem>
            </Select>
          </FormControl>

          {/* Available For - Listing Status */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel sx={{ color: palette.grey[600] }}>Listing Status</InputLabel>
            <Select
              value={availableFor}
              label="Listing Status"
              onChange={(e) => setAvailableFor(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: palette.inputborder
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: palette.primary.main
                }
              }}
            >
              <MenuItem value="">Any</MenuItem>
              {Array.isArray(listingOptions) &&
                listingOptions.map((listingItem) => (
                  <MenuItem
                    key={listingItem?.id || listingItem?.name}
                    value={listingItem?.name || listingItem?.id}
                  >
                    {listingItem?.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* Area Units*/}

          {/* Area Units Dropdown */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel sx={{ color: palette.grey[600] }}>Area Units</InputLabel>
            <Select
              value={areaUnits}
              label="Area Units"
              onChange={(e) => setAreaUnits(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: palette.inputborder
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: palette.primary.main
                }
              }}
            >
              <MenuItem value="">Any</MenuItem>
              {Array.isArray(areaUnitsOptions) &&
                areaUnitsOptions.map((areaUnit) => (
                  <MenuItem
                    key={areaUnit?.id || areaUnit?.name}
                    value={areaUnit?.name || areaUnit?.id}
                  >
                    {areaUnit?.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <Divider sx={{ my: 3, bgcolor: palette.grey[200] }} />

          {/* Featured Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={isFeatured}
                onChange={(e) => setIsFeatured(e.target.checked)}
                sx={{
                  color: palette.primary.main,
                  '&.Mui-checked': {
                    color: palette.primary.main
                  }
                }}
              />
            }
            label={
              <Typography sx={{ color: palette.primary.title, fontWeight: 500 }}>
                Featured Properties Only
              </Typography>
            }
          />

          {/* Garage Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={isGarage}
                onChange={(e) => setIsGarage(e.target.checked)}
                sx={{
                  color: palette.primary.main,
                  '&.Mui-checked': {
                    color: palette.primary.main
                  }
                }}
              />
            }
            label={
              <Typography sx={{ color: palette.primary.title, fontWeight: 500 }}>
                Properties with Garage
              </Typography>
            }
          />
        </Box>

        {/* Footer Actions */}
        <Box
          sx={{
            p: 3,
            borderTop: `1px solid ${palette.grey[200]}`,
            background: palette.common.white,
            display: 'flex',
            gap: 2
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={handleResetFilters}
            sx={{
              borderColor: palette.grey[400],
              color: palette.grey[700],
              '&:hover': {
                borderColor: palette.grey[600],
                background: palette.grey[50]
              }
            }}
          >
            Reset
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleApplyFilters}
            sx={{
              background: palette.primary.main,
              color: palette.primary.contrastText,
              '&:hover': {
                background: palette.primary.dark
              }
            }}
          >
            Apply Filters
          </Button>
        </Box>

        {/* Year Picker Popover */}
        <Popover
          open={yearPickerOpen}
          anchorEl={yearAnchorEl}
          onClose={closeYearPicker}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          sx={{
            '& .MuiPopover-paper': {
              width: isMobile ? '90vw' : 320,
              maxWidth: '100%',
              maxHeight: 400,
              borderRadius: 2,
              boxShadow: `0 8px 32px ${alpha(palette.common.black, 0.2)}`
            }
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
                pb: 1,
                borderBottom: `1px solid ${palette.grey[200]}`
              }}
            >
              <Typography variant="h6" sx={{ color: palette.primary.title, fontWeight: 600 }}>
                Select Build Year
              </Typography>
              <Button
                size="small"
                onClick={clearBuildYear}
                sx={{
                  color: palette.primary.main,
                  fontSize: '0.75rem'
                }}
              >
                Clear
              </Button>
            </Box>

            <Grid container spacing={1} sx={{ maxHeight: 300, overflow: 'auto' }}>
              {years.map((year) => (
                <Grid item xs={4} key={year}>
                  <Button
                    fullWidth
                    variant={buildYear === year ? 'contained' : 'outlined'}
                    onClick={() => handleYearSelect(year)}
                    sx={{
                      height: 40,
                      fontSize: '0.875rem',
                      background: buildYear === year ? palette.primary.main : 'transparent',
                      color:
                        buildYear === year ? palette.primary.contrastText : palette.primary.title,
                      borderColor: buildYear === year ? palette.primary.main : palette.grey[300],
                      '&:hover': {
                        background: buildYear === year ? palette.primary.dark : palette.grey[50],
                        borderColor: palette.primary.main
                      }
                    }}
                  >
                    {year}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Popover>
      </Drawer>
    </>
  );
};

export default FilterDrawer;
